import React, { useState } from 'react';
import { Spinner } from 'reactstrap';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { useSessionUrlActivities } from '@/hooks/useSessionUrlActivities';
import { Table } from '@/components/Table';
import { InitialsBadge } from '@/components/InitialsBadge';
import { ProgressBar } from '@/components/ProgressBar';
import { Text, textVariantsEnum, fontWeightEnum } from '@/components/Text';
import { formatTimeFromSeconds } from '@/utils/timeUtils';
import { formatPercent } from '@/utils/numberUtils';
import { urlActivitiesTableConfig } from '../UrlTracking.config';
import { EmptyState } from '@/components/EmptyState';
import styles from './DomainUsageTable.module.scss';

// Domains expand to reveal individual URLs — a nesting the generic <Table>
// doesn't support for its own rows — so the outer domain list stays
// hand-rolled (as it was before this redesign) while everything else
// (icon badge, progress bar, tokens, per-URL Table) matches AppUsage's
// per-app table so the two "session breakdown" tables read as one system.
export const DomainUsageTableComponent = ({
  session,
  teamId,
  userId,
  demoUrlActivities,
}) => {
  const [expandedDomains, setExpandedDomains] = useState(new Set());

  const {
    data: fetchedUrlActivities,
    isLoading,
    isError,
  } = useSessionUrlActivities(teamId, userId, session.id, {
    enabled: !demoUrlActivities && expandedDomains.size > 0,
  });

  const urlActivities = demoUrlActivities || fetchedUrlActivities;

  const domainRows = Object.entries(session.urlUsage || {})
    .map(([domain, seconds]) => ({ domain, seconds: Number(seconds) || 0 }))
    .filter((r) => r.seconds > 0)
    .sort((a, b) => b.seconds - a.seconds);

  if (!domainRows.length) {
    return (
      <EmptyState
        illustration
        title="No URL Data"
        description="No URL tracking data for this session."
      />
    );
  }

  const sessionTotalSeconds = domainRows.reduce((sum, r) => sum + r.seconds, 0);

  const toggleDomain = (domain) => {
    setExpandedDomains((prev) => {
      const next = new Set(prev);
      next.has(domain) ? next.delete(domain) : next.add(domain);
      return next;
    });
  };

  return (
    <div className={styles.domainList}>
      <div className={styles.domainListHeader}>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Semibold}
          className={styles.numCol}
        >
          #
        </Text>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Semibold}
          className={styles.domainCol}
        >
          DOMAIN
        </Text>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Semibold}
          className={styles.timeCol}
        >
          TIME SPENT
        </Text>
        <Text
          variant={textVariantsEnum.Text_13}
          fontWeight={fontWeightEnum.Semibold}
          className={styles.percentCol}
        >
          % OF TIME
        </Text>
        <span className={styles.expandCol} />
      </div>

      {domainRows.map((row, idx) => {
        const isExpanded = expandedDomains.has(row.domain);
        const percent = sessionTotalSeconds
          ? (row.seconds / sessionTotalSeconds) * 100
          : 0;
        const domainActivities = (urlActivities || []).filter(
          (a) => a.domain === row.domain
        );

        return (
          <div key={row.domain} className={styles.domainRow}>
            <div
              className={styles.domainRowHeader}
              onClick={() => toggleDomain(row.domain)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => e.key === 'Enter' && toggleDomain(row.domain)}
            >
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Medium}
                className={styles.numCol}
              >
                {idx + 1}
              </Text>
              <div className={styles.domainCell}>
                <InitialsBadge label={row.domain} />
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Medium}
                  className={styles.domainName}
                >
                  {row.domain}
                </Text>
              </div>
              <Text
                variant={textVariantsEnum.Text_13}
                fontWeight={fontWeightEnum.Medium}
                className={styles.timeCol}
              >
                {formatTimeFromSeconds(row.seconds)}
              </Text>
              <div className={styles.percentCol}>
                <ProgressBar percent={percent} color="blue-500" />
                <Text
                  variant={textVariantsEnum.Text_13}
                  fontWeight={fontWeightEnum.Medium}
                  className={styles.percentValue}
                >
                  {formatPercent(percent)}
                </Text>
              </div>
              <span className={styles.expandCol}>
                {isExpanded ? <FiChevronDown /> : <FiChevronRight />}
              </span>
            </div>

            {isExpanded && (
              <div className={styles.domainDetail}>
                {isLoading ? (
                  <div className={styles.detailLoader}>
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : isError ? (
                  <Text
                    variant={textVariantsEnum.Text_sm}
                    color="gray-500"
                    className={styles.detailMessage}
                  >
                    Failed to load URL details.
                  </Text>
                ) : domainActivities.length === 0 ? (
                  <EmptyState
                    illustration
                    title="No URL Data"
                    description="No URL-level data available for this domain."
                  />
                ) : (
                  <Table
                    className={styles.urlDetailTable}
                    data={domainActivities}
                    columns={urlActivitiesTableConfig()}
                  />
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

DomainUsageTableComponent.displayName = 'DomainUsageTable';
